import { Component, OnInit, Input, OnChanges, SimpleChanges, HostListener, ViewChild, ChangeDetectorRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { PlanService, Plan } from '../services/plan.service';
import { ObjektStrukturService, Sektion } from '../services/objekt-struktur.service';
import { DokumenteService, FileUpload } from '../services/dokumente.service';
import { StandortService, Standort } from '../services/standort.service';
import { KundeService, Kunde } from '../services/kunde.service';
import { environment } from '../../environments/environment';
import { InteractivePlanComponent } from '../components/interactive-plan/interactive-plan.component';
import { EditIndicatorDialogComponent } from '../components/objekt-struktur-treetable/edit-indicator-dialog/edit-indicator-dialog.component';

interface DropdownData {
  sektionen?: Sektion[];
  standorte?: Standort[];
  clients?: Kunde[];
  uploads?: FileUpload[];
}

export interface RoomIndicator {
  id: string;
  name: string;
  current_value: string;
  einheit: string;
  schwellenwert: number;
  status: number;
  room_id: string;
  indikator_id: string;
  bemerkung?: string;
  qr_code?: string;
  mehr_weniger: number;
  indikator_number?: number;
  position_x?: number | null;
  position_y?: number | null;
  system_id?: number;
  systemtyp_caption?: string;
  created_at?: string;
  updated_at?: string;
  room?: {
    id: string;
    name: string;
    sektion?: {
      id: string;
      name: string;
      gebaeude?: {
        id: string;
        name: string;
      };
    };
  };
  indikator?: {
    id: string;
    name: string;
    code?: string;
    symbol_id?: string;
  };
}

interface PlacedIndicator {
  id: string;
  indicator: RoomIndicator;
  x: number; // Position relative to image (0-100%)
  y: number; // Position relative to image (0-100%)
  isDragging?: boolean;
}

@Component({
  selector: 'app-plan-edit',
  templateUrl: './plan-edit.component.html',
  styleUrls: ['./plan-edit.component.scss']
})
export class PlanEditComponent implements OnInit, OnChanges {
  @Input() planId?: string;
  @Input() isEmbedded: boolean = false;
  @Input() parentContext?: string;
  @ViewChild('interactivePlan') interactivePlanComponent?: InteractivePlanComponent;

  planForm!: UntypedFormGroup;
  plan?: Plan;
  isEditMode = false;
  isLoading = true;
  isSaving = false;
  dropdowns?: DropdownData;
  roomIndicators?: RoomIndicator[];
  
  // Current context
  currentStandortId?: string;
  currentClientId?: string;
  currentStandort?: Standort;
  currentClient?: Kunde;
  
  // File dropdown state
  isFileDropdownOpen = false;
  selectedUpload?: FileUpload;
  cachedInteractivePlanFile?: FileUpload | null;
  
  // Plan dropdown state for interactive preview
  isPlanDropdownOpen = false;
  selectedPreviewPlan?: Plan;
  availablePlans: Plan[] = [];
  
  // Sektion selection state
  selectedSektion: Sektion | null = null;
  showSektionDropdown = false;
  
  // Preview image state
  isPreviewImageLoading = false;
  previewImageLoadError = false;
  
  // Zoom state
  zoomLevel = 100;
  minZoom = 25;
  maxZoom = 300;
  
  // Drag and drop state
  placedIndicators: PlacedIndicator[] = [];
  draggedIndicator: RoomIndicator | null = null;
  isDragging = false;
  
  
  // Tooltip state
  showTooltip = false;
  tooltipX = 0;
  tooltipY = 0;
  tooltipIndicator: PlacedIndicator | null = null;
  
  // Image preview state
  showImagePreview = false;
  isImageLoading = false;
  imageLoadError = false;
  
  // PDF export state
  isExportingPDF = false;

  constructor(
    private fb: UntypedFormBuilder,
    private planService: PlanService,
    private objektStrukturService: ObjektStrukturService,
    private dokumenteService: DokumenteService,
    private standortService: StandortService,
    private kundeService: KundeService,
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.initForm();
    this.loadDropdowns();
    this.loadPlan();
    this.loadAvailablePlans();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['planId'] && !changes['planId'].firstChange) {
      this.loadPlan();
    }
  }

  initForm(): void {
    this.planForm = this.fb.group({
      bezeichnung: ['', Validators.required],
      sektion_id: ['', Validators.required],
      standort_id: [''],
      client_id: [''],
      upload_id: [''],
      active: [true],
      anmerkungen: ['']
    });
  }

  loadDropdowns(): void {
    // Get standortId from query parameters
    this.route.queryParams.subscribe(params => {
      this.currentStandortId = params['standortId'];
      this.currentClientId = params['clientId'];
      
      if (this.currentStandortId) {
        this.loadStandortData();
        this.loadSektionen();
        this.loadUploads();
      }
      
      if (this.currentClientId) {
        this.loadClientData();
      }
    });
  }

  loadDropdownsForPlan(plan: Plan): void {
    // Load dropdown data for an existing plan
    if (plan.standort_id) {
      this.currentStandortId = plan.standort_id;
      this.loadStandortData();
      this.loadSektionen();
      this.loadUploads();
    }
    
    // Don't load client data separately - it will be loaded from standort's kunde_id
    // The loadStandortData() method will automatically set currentClientId and load client data
  }

  loadStandortData(): void {
    if (!this.currentStandortId) return;
    
    this.standortService.getStandort(this.currentStandortId).subscribe({
      next: (response) => {
        this.currentStandort = response.data;
        this.dropdowns = {
          ...this.dropdowns,
          standorte: [response.data]
        };
        
        // Always use the standort's kunde_id as the client ID
        if (response.data.kunde_id) {
          this.currentClientId = response.data.kunde_id;
          console.log('loadStandortData - Setting client ID from standort:', this.currentClientId);
          
          // Update the form with the client_id from standort
          this.planForm.patchValue({
            client_id: this.currentClientId
          });
          
          // Load client data
          this.loadClientData();
        }
      },
      error: (error) => {
        console.error('Error loading standort:', error);
        this.snackBar.open('Fehler beim Laden des Standorts', 'Schließen', { duration: 3000 });
      }
    });
  }

  loadClientData(): void {
    if (!this.currentClientId) return;
    
    this.kundeService.getKundeByUuid(this.currentClientId).subscribe({
      next: (response) => {
        this.currentClient = response.data;
        this.dropdowns = {
          ...this.dropdowns,
          clients: [response.data]
        };
        
        // Update the form with the client_id
        this.planForm.patchValue({
          client_id: this.currentClientId
        });
      },
      error: (error) => {
        console.error('Error loading client:', error);
        this.snackBar.open('Fehler beim Laden des Kunden', 'Schließen', { duration: 3000 });
      }
    });
  }

  loadSektionen(): void {
    if (!this.currentStandortId) {
      console.warn('loadSektionen: No currentStandortId available');
      return;
    }
    
    console.log('loadSektionen: Loading sektionen for standort:', this.currentStandortId);
    this.objektStrukturService.getSektionenByStandort(this.currentStandortId).subscribe({
      next: (response) => {
        console.log('Sektionen response:', response);
        const sektionen = response.data || [];
        console.log('Loaded sektionen count:', sektionen.length);
        console.log('Sektionen data:', sektionen);
        
        this.dropdowns = {
          ...this.dropdowns,
          sektionen: sektionen
        };
        
        // Set selected sektion if plan has one
        if (this.plan?.sektion_id && this.dropdowns?.sektionen) {
          this.selectedSektion = this.dropdowns.sektionen.find(s => s.id === this.plan?.sektion_id) || null;
          console.log('Selected sektion:', this.selectedSektion);
        }
      },
      error: (error) => {
        console.error('Error loading sektionen:', error);
        console.error('Error details:', error);
        this.snackBar.open('Fehler beim Laden der Sektionen', 'Schließen', { duration: 3000 });
      }
    });
  }

  /**
   * Sektion dropdown selection handler
   */
  selectSektion(sektion: Sektion): void {
    this.selectedSektion = sektion;
    this.planForm.patchValue({ sektion_id: sektion.id });
    this.planForm.get('sektion_id')?.markAsTouched();
    this.showSektionDropdown = false;
  }

  onSektionDropdownBlur(): void {
    // Delay closing to allow click events to fire
    setTimeout(() => {
      this.showSektionDropdown = false;
    }, 200);
  }

  loadUploads(): void {
    if (!this.currentStandortId) return;
    
    // Load standort files
    this.dokumenteService.getStandortDokumente(this.currentStandortId, 1, 100).subscribe({
      next: (response) => {
        // Handle both array and object response types
        let uploads: FileUpload[] = [];
        if (Array.isArray(response.data)) {
          uploads = response.data;
        } else if (typeof response.data === 'object') {
          // Flatten grouped data
          uploads = Object.values(response.data).flat();
        }
        
        // Also load plan-specific files if we have a plan
        if (this.plan?.id) {
          this.dokumenteService.getDokumente('plan', this.plan.id, 1, 100).subscribe({
            next: (planResponse) => {
              let planUploads: FileUpload[] = [];
              if (Array.isArray(planResponse.data)) {
                planUploads = planResponse.data;
              } else if (typeof planResponse.data === 'object') {
                planUploads = Object.values(planResponse.data).flat();
              }
              
              // Merge plan files with standort files, avoiding duplicates
              const existingIds = new Set(uploads.map(u => u.id));
              const newPlanUploads = planUploads.filter(u => !existingIds.has(u.id));
              uploads = [...uploads, ...newPlanUploads];
              
              this.updateUploadsList(uploads);
            },
            error: (error) => {
              console.error('Error loading plan files:', error);
              // Continue with standort files only
              this.updateUploadsList(uploads);
            }
          });
        } else {
          this.updateUploadsList(uploads);
        }
      },
      error: (error) => {
        console.error('Error loading uploads:', error);
        this.snackBar.open('Fehler beim Laden der Dateien', 'Schließen', { duration: 3000 });
      }
    });
  }

  private updateUploadsList(uploads: FileUpload[]): void {
    // Clear cached interactive plan file when uploads change
    this.clearInteractivePlanFileCache();
    this.dropdowns = {
      ...this.dropdowns,
      uploads: uploads
    };
    
    // If we have a plan with upload_id but no selectedUpload yet, set it now
    if (this.plan?.upload_id && !this.selectedUpload) {
      const uploadIdNum = parseInt(this.plan.upload_id.toString(), 10);
      this.selectedUpload = uploads.find(u => u.id === uploadIdNum);
    }
    
    // Trigger change detection to update the view
    this.cdr.detectChanges();
  }

  /**
   * Called when the interactive plan is saved
   * Refreshes the uploads list to include the newly saved interactive plan file
   */
  onInteractivePlanSaved(): void {
    // Refresh uploads to include the newly saved interactive plan file
    this.loadUploads();
  }

  loadPlan(): void {
    // Get plan ID from route parameters or input
    let planId = this.planId;
    
    if (!planId) {
      // Try to get from route parameters synchronously
      planId = this.route.snapshot.params['id'];
    }
    
    this.loadPlanById(planId);
  }

  loadPlanById(planId: string | undefined): void {
    if (!planId || planId === 'new') {
      this.isLoading = false;
      this.isEditMode = true;
      this.planForm.enable();
      
      // Handle query parameters for new plan creation
      this.route.queryParams.subscribe(params => {
        if (params['standortId']) {
          this.currentStandortId = params['standortId'];
          this.planForm.patchValue({
            standort_id: params['standortId']
          });
          // Load standort data to get the client ID automatically
          this.loadStandortData();
        }
        if (params['sektionId']) {
          this.planForm.patchValue({
            sektion_id: params['sektionId']
          });
        }
        // Don't set clientId from params - it should come from standort's kunde_id
        // The loadStandortData() method will handle setting the client_id
      });
      
      return;
    }

    this.isLoading = true;
    this.planService.getPlan(planId).subscribe({
      next: (plan) => {
        this.plan = plan;
        
        // Set current context from the loaded plan
        this.currentStandortId = plan.standort_id;
        this.currentClientId = plan.client_id;
        
        // Load dropdown data for the plan's standort
        this.loadDropdownsForPlan(plan);
        
        this.planForm.patchValue({
          bezeichnung: plan.bezeichnung,
          sektion_id: plan.sektion_id,
          standort_id: plan.standort_id,
          client_id: plan.client_id,
          upload_id: plan.upload_id,
          active: plan.active,
          anmerkungen: plan.anmerkungen
        });
        this.planForm.disable(); // Start in view mode
        
      // Set selected sektion if plan has one (after sektionen are loaded)
      if (plan.sektion_id && this.dropdowns?.sektionen) {
        this.selectedSektion = this.dropdowns.sektionen.find(s => s.id === plan.sektion_id) || null;
      }
        
        // Set selected upload for existing plans
        if (plan.upload_id && this.dropdowns?.uploads) {
          const uploadIdNum = parseInt(plan.upload_id.toString(), 10);
          this.selectedUpload = this.dropdowns.uploads.find(u => u.id === uploadIdNum);
        }
        
        this.loadRoomIndicators();
        this.isLoading = false;
        
        // Request signed URL for the plan image if it's an image file
        const selectedUpload = this.getSelectedUpload();
        if (selectedUpload && this.isImageFile(selectedUpload)) {
          this.requestSignedUrl(selectedUpload.id as number, false);
        }
      },
      error: (error) => {
        console.error('Error loading plan:', error);
        this.snackBar.open('Fehler beim Laden des Plans', 'Schließen', { duration: 3000 });
        this.isLoading = false;
      }
    });
  }

  loadRoomIndicators(): void {
    // Load room indicators for this plan's standort, filtered by sektion and active status
    // This filtered list is used by both the Indikatoren tab and the interactive plan sidebar
    if (!this.currentStandortId) {
      console.warn('No standort ID available for loading room indicators');
      this.roomIndicators = [];
      return;
    }

    this.objektStrukturService.getRoomIndikatorenByStandort(this.currentStandortId, 200).subscribe({
      next: (response: any) => {
        // Handle both array response and paginated response
        let allIndicators: RoomIndicator[] = [];
        if (Array.isArray(response)) {
          allIndicators = response;
        } else if (response.data && Array.isArray(response.data)) {
          allIndicators = response.data;
        } else {
          allIndicators = [];
        }
        
        // Filter by sektion (if plan has sektion_id) and only effectively active indicators
        // This ensures both the Indikatoren tab and interactive plan show the same filtered list
        // Note: status can be boolean true or number 1
        let filtered: RoomIndicator[];
        if (this.plan?.sektion_id) {
          filtered = allIndicators.filter(indicator => {
            const matchesSektion = indicator.room?.sektion?.id === this.plan?.sektion_id;
            return matchesSektion && this.isIndicatorEffectivelyActive(indicator);
          });
        } else {
          // If no sektion_id, just filter by effective status
          filtered = allIndicators.filter(indicator => this.isIndicatorEffectivelyActive(indicator));
        }
        
        // Sort by indikator_number
        this.roomIndicators = this.sortIndicatorsByNumber(filtered);
        
        console.log('Room indicators loaded (filtered by sektion and active):', this.roomIndicators?.length || 0);
      },
      error: (error) => {
        console.error('Error loading room indicators:', error);
        this.snackBar.open('Fehler beim Laden der Raum-Indikatoren', 'Schließen', { duration: 3000 });
        this.roomIndicators = [];
      }
    });
  }

  toggleEditMode(): void {
    this.isEditMode = !this.isEditMode;
    if (this.isEditMode) {
      this.planForm.enable();
      
      // Set selected sektion when entering edit mode
      const sektionId = this.planForm.get('sektion_id')?.value;
      if (sektionId && this.dropdowns?.sektionen) {
        this.selectedSektion = this.dropdowns.sektionen.find(s => s.id === sektionId) || null;
      }
    } else {
      this.planForm.disable();
    }
  }

  savePlan(): void {
    if (this.planForm.invalid) {
      return;
    }

    // Validate that sektion is active if sektion_id is provided
    const sektionId = this.planForm.value.sektion_id;
    if (sektionId && this.dropdowns?.sektionen) {
      const sektion = this.dropdowns.sektionen.find(s => s.id === sektionId);
      if (sektion && (sektion as any).status !== 1 && (sektion as any).status !== true) {
        this.snackBar.open('Plan kann nicht gespeichert werden: Die zugehörige Sektion ist inaktiv', 'Schließen', { duration: 5000 });
        return;
      }
    }

    this.isSaving = true;
    const planData = this.planForm.value;

    if (this.plan) {
      // Update existing plan
      this.planService.updatePlan(this.plan.id, planData).subscribe({
        next: (updatedPlan) => {
          this.plan = updatedPlan;
          this.isEditMode = false;
          this.planForm.disable();
          this.snackBar.open('Plan erfolgreich aktualisiert', 'Schließen', { duration: 3000 });
          this.isSaving = false;
        },
        error: (error) => {
          console.error('Error updating plan:', error);
          this.snackBar.open('Fehler beim Aktualisieren des Plans', 'Schließen', { duration: 3000 });
          this.isSaving = false;
        }
      });
    } else {
      // Create new plan
      this.planService.createPlan(planData).subscribe({
        next: (newPlan) => {
          this.plan = newPlan;
          this.isEditMode = false;
          this.planForm.disable();
          this.snackBar.open('Plan erfolgreich erstellt', 'Schließen', { duration: 3000 });
          this.isSaving = false;
          
          // Navigate to the new plan
          if (!this.isEmbedded) {
            this.router.navigate(['/plan', newPlan.id]);
          }
        },
        error: (error) => {
          console.error('Error creating plan:', error);
          this.snackBar.open('Fehler beim Erstellen des Plans', 'Schließen', { duration: 3000 });
          this.isSaving = false;
        }
      });
    }
  }

  cancelEdit(): void {
    if (this.plan) {
      this.planForm.patchValue({
        bezeichnung: this.plan.bezeichnung,
        sektion_id: this.plan.sektion_id,
        standort_id: this.plan.standort_id,
        client_id: this.plan.client_id,
        upload_id: this.plan.upload_id,
        active: this.plan.active,
        anmerkungen: this.plan.anmerkungen
      });
      
      // Update selected sektion after save
      if (this.plan?.sektion_id && this.dropdowns?.sektionen) {
        this.selectedSektion = this.dropdowns.sektionen.find(s => s.id === this.plan?.sektion_id) || null;
      }
      
      this.isEditMode = false;
      this.planForm.disable();
    } else {
      this.goBack();
    }
  }

  deletePlan(): void {
    if (!this.plan) return;

    if (confirm(`Möchten Sie den Plan "${this.plan.bezeichnung}" wirklich löschen?`)) {
      this.planService.deletePlan(this.plan.id).subscribe({
        next: () => {
          this.snackBar.open('Plan erfolgreich gelöscht', 'Schließen', { duration: 3000 });
          this.goBack();
        },
        error: (error) => {
          console.error('Error deleting plan:', error);
          this.snackBar.open('Fehler beim Löschen des Plans', 'Schließen', { duration: 3000 });
        }
      });
    }
  }

  goBack(): void {
    // Check if we have return context from query parameters
    this.route.queryParams.subscribe(params => {
      const returnTo = params['returnTo'];
      const tab = params['tab'];
      
      if (returnTo === 'standort-edit' && this.currentStandortId) {
        // Navigate back to standort-edit with specific tab
        this.router.navigate(['/standort-edit', this.currentStandortId], { 
          queryParams: { tab: tab || 'plaene' } 
        });
        return;
      }
      
      // Case 1: Embedded in standort-edit context
      if (this.isEmbedded && this.parentContext === 'standort-edit') {
        return; // Stay in same context
      }
      
      // Case 2: Plan has standort - navigate to standort-edit
      if (this.plan?.standort_id) {
        this.router.navigate(['/standort-edit', this.plan.standort_id]);
        return;
      }
      
      // Case 3: Creating new plan - use standortId from query parameters
      if (this.currentStandortId) {
        this.router.navigate(['/standort-edit', this.currentStandortId]);
        return;
      }
      
      // Case 4: Fallback to plan list
      this.router.navigate(['/plan']);
    });
  }

  downloadPlan(): void {
    if (this.plan?.upload) {
      // Implement plan download functionality
      this.snackBar.open('Download gestartet...', 'Schließen', { duration: 2000 });
    }
  }

  uploadPlan(): void {
    // Implement plan upload functionality
    this.snackBar.open('Plan-Upload-Funktion wird implementiert...', 'Schließen', { duration: 2000 });
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.planForm.get(fieldName);
    return field ? field.invalid && (field.dirty || field.touched) : false;
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getIndicatorStatusClass(indicator: RoomIndicator): string {
    const value = parseFloat(indicator.current_value);
    const threshold = indicator.schwellenwert;
    
    if (value > threshold) {
      return 'text-red-600';
    } else if (value > threshold * 0.8) {
      return 'text-yellow-600';
    } else {
      return 'text-green-600';
    }
  }

  getIndicatorStatusClasses(indicator: RoomIndicator): string {
    const value = parseFloat(indicator.current_value);
    const threshold = indicator.schwellenwert;
    
    // Check if mehr_weniger is 1 (more is better) or 0 (less is better)
    const isMoreBetter = indicator.mehr_weniger === 1;
    
    if (isMoreBetter) {
      // For "more is better" indicators
      if (value < threshold * 0.8) {
        return 'bg-red-50 text-red-700 ring-red-600/20';
      } else if (value < threshold) {
        return 'bg-yellow-50 text-yellow-700 ring-yellow-600/20';
      } else {
        return 'bg-green-50 text-green-700 ring-green-600/20';
      }
    } else {
      // For "less is better" indicators
      if (value > threshold) {
        return 'bg-red-50 text-red-700 ring-red-600/20';
      } else if (value > threshold * 0.8) {
        return 'bg-yellow-50 text-yellow-700 ring-yellow-600/20';
      } else {
        return 'bg-green-50 text-green-700 ring-green-600/20';
      }
    }
  }

  getIndicatorIcon(indicator: RoomIndicator): string {
    const value = parseFloat(indicator.current_value);
    const threshold = indicator.schwellenwert;
    const isMoreBetter = indicator.mehr_weniger === 1;
    
    if (isMoreBetter) {
      // For "more is better" indicators
      if (value < threshold * 0.8) {
        return 'warning';
      } else if (value < threshold) {
        return 'info';
      } else {
        return 'check_circle';
      }
    } else {
      // For "less is better" indicators
      if (value > threshold) {
        return 'warning';
      } else if (value > threshold * 0.8) {
        return 'info';
      } else {
        return 'check_circle';
      }
    }
  }

  getIndicatorStatusText(indicator: RoomIndicator): string {
    const value = parseFloat(indicator.current_value);
    const threshold = indicator.schwellenwert;
    const isMoreBetter = indicator.mehr_weniger === 1;
    
    if (isMoreBetter) {
      // For "more is better" indicators
      if (value < threshold * 0.8) {
        return 'Kritisch';
      } else if (value < threshold) {
        return 'Warnung';
      } else {
        return 'Normal';
      }
    } else {
      // For "less is better" indicators
      if (value > threshold) {
        return 'Kritisch';
      } else if (value > threshold * 0.8) {
        return 'Warnung';
      } else {
        return 'Normal';
      }
    }
  }

  editRoomIndicator(indicator: any): void {
    const standortId = this.currentStandortId || this.plan?.standort_id;
    if (!standortId) {
      this.snackBar.open('Standort-ID fehlt – Indikator kann nicht bearbeitet werden', 'Schließen', { duration: 4000 });
      return;
    }

    if (!indicator?.id) {
      this.snackBar.open('Indikator-ID fehlt – Indikator kann nicht bearbeitet werden', 'Schließen', { duration: 4000 });
      return;
    }

    // The plan table emits a lightweight row object; fetch the full RoomIndikator (incl. room_id) first.
    this.objektStrukturService.getRoomIndikatorById(indicator.id).subscribe({
      next: (resp: any) => {
        // API may return the object directly OR wrapped in { data: ... }
        const full = (resp && resp.data) ? resp.data : resp;

        if (!full?.id || !full?.room_id) {
          console.error('Room-indikator response missing required fields:', resp);
          this.snackBar.open('Fehler: Indikator-Daten unvollständig', 'Schließen', { duration: 5000 });
          return;
        }

        const dialogRef = this.dialog.open(EditIndicatorDialogComponent, {
          width: '600px',
          data: {
            id: full.id,
            roomId: full.room_id,
            standortId,
            currentData: full as any,
            indikatorOptions: []
          }
        });

        dialogRef.afterClosed().subscribe((result) => {
          if (result?.event === 'update') {
            // Reload from API so table + interactive plan sidebar reflect the latest data
            this.loadRoomIndicators();
            this.snackBar.open('Indikator gespeichert', 'Schließen', { duration: 2500 });
          }
        });
      },
      error: (error) => {
        console.error('Error loading room-indikator by id:', error);
        this.snackBar.open('Fehler beim Laden des Indikators', 'Schließen', { duration: 4000 });
      }
    });
  }

  getStandortName(standortId: string): string {
    if (!standortId || !this.dropdowns?.standorte) return '';
    const standort = this.dropdowns.standorte.find(s => s.id === standortId);
    return standort ? standort.name : '';
  }

  getClientName(clientId: string): string {
    if (!clientId || !this.dropdowns?.clients) return '';
    
    const client = this.dropdowns.clients.find(c => {
      // Safety check to prevent undefined errors
      if (!c) return false;
      return (c.uuid && c.uuid === clientId) || (c.id && c.id.toString() === clientId);
    });
    
    return client ? client.suchbegriff : '';
  }

  getUploadName(uploadId: string): string {
    if (!uploadId || !this.dropdowns?.uploads) return '';
    const uploadIdNum = parseInt(uploadId, 10);
    const upload = this.dropdowns.uploads.find(u => u.id === uploadIdNum);
    return upload ? (upload.original_filename || upload.filename || `Upload ${upload.id}`) : '';
  }

  getUploadDisplayText(upload: FileUpload): string {
    const filename = upload.original_filename || upload.filename || 'Unbekannte Datei';
    const fileSize = this.formatFileSize(upload.file_size || 0);
    const uploadDate = this.formatDate(upload.uploadDate || '');
    return `${filename} (${fileSize}) - ${uploadDate}`;
  }

  getSelectedUpload(): FileUpload | null {
    const uploadId = this.planForm.get('upload_id')?.value;
    if (!uploadId || !this.dropdowns?.uploads) return null;
    const uploadIdNum = parseInt(uploadId, 10);
    return this.dropdowns.uploads.find(u => u.id === uploadIdNum) || null;
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('de-DE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  }

  getBackButtonTooltip(): string {
    // Case 1: Embedded in standort-edit context
    if (this.isEmbedded && this.parentContext === 'standort-edit') {
      return 'Im Standort-Edit bleiben';
    }
    
    // Case 2: Plan has standort - show standort navigation
    if (this.plan?.standort_id) {
      const standortName = this.getStandortName(this.plan.standort_id);
      return standortName ? `Zurück zu Standort: ${standortName}` : 'Zurück zu Standort';
    }
    
    // Case 3: Creating new plan - show standort navigation
    if (this.currentStandortId) {
      const standortName = this.currentStandort?.name || 'Standort';
      return `Zurück zu Standort: ${standortName}`;
    }
    
    // Case 4: Fallback to plan list
    return 'Zurück zu Pläne';
  }

  // Drag and Drop Methods for Interactive Plan Preview
  onDragStart(event: DragEvent, indicator: RoomIndicator): void {
    if (event.dataTransfer) {
      event.dataTransfer.setData('text/plain', JSON.stringify(indicator));
      event.dataTransfer.effectAllowed = 'copy';
      this.draggedIndicator = indicator;
    }
  }

  onImageDragOver(event: DragEvent): void {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
  }

  onImageDrop(event: DragEvent): void {
    event.preventDefault();
    if (event.dataTransfer && this.draggedIndicator) {
      const rect = (event.target as HTMLElement).getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;
      
      // Create a new placed indicator
      const placedIndicator: PlacedIndicator = {
        id: `placed_${Date.now()}_${this.draggedIndicator.id}`,
        indicator: this.draggedIndicator,
        x: Math.max(0, Math.min(100, x)), // Clamp between 0-100%
        y: Math.max(0, Math.min(100, y))  // Clamp between 0-100%
      };
      
      this.placedIndicators.push(placedIndicator);
      this.draggedIndicator = null;
      
      this.snackBar.open(`Indikator "${placedIndicator.indicator.name}" wurde platziert`, 'Schließen', { duration: 2000 });
    }
  }

  onPlacedIndicatorDragStart(event: DragEvent, placedIndicator: PlacedIndicator): void {
    event.stopPropagation();
    this.isDragging = true;
    placedIndicator.isDragging = true;
  }

  onPlacedIndicatorDragEnd(event: DragEvent, placedIndicator: PlacedIndicator): void {
    this.isDragging = false;
    placedIndicator.isDragging = false;
  }

  onPlacedIndicatorMouseMove(event: MouseEvent): void {
    if (this.isDragging) {
      const draggingIndicator = this.placedIndicators.find(pi => pi.isDragging);
      if (draggingIndicator) {
        const imageContainer = (event.target as HTMLElement).closest('.image-container');
        if (imageContainer) {
          const rect = imageContainer.getBoundingClientRect();
          const x = ((event.clientX - rect.left) / rect.width) * 100;
          const y = ((event.clientY - rect.top) / rect.height) * 100;
          
          draggingIndicator.x = Math.max(0, Math.min(100, x));
          draggingIndicator.y = Math.max(0, Math.min(100, y));
        }
      }
    }
  }

  removePlacedIndicator(placedIndicator: PlacedIndicator): void {
    const index = this.placedIndicators.findIndex(pi => pi.id === placedIndicator.id);
    if (index > -1) {
      this.placedIndicators.splice(index, 1);
      this.snackBar.open(`Indikator "${placedIndicator.indicator.name}" wurde entfernt`, 'Schließen', { duration: 2000 });
    }
  }

  // Tooltip methods
  showIndicatorTooltip(event: MouseEvent, placedIndicator: PlacedIndicator): void {
    this.tooltipIndicator = placedIndicator;
    this.tooltipX = event.clientX;
    this.tooltipY = event.clientY;
    this.showTooltip = true;
  }

  hideIndicatorTooltip(): void {
    this.showTooltip = false;
    this.tooltipIndicator = null;
  }

  // File dropdown methods
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (this.isFileDropdownOpen) {
      this.isFileDropdownOpen = false;
    }
    if (this.isPlanDropdownOpen) {
      this.isPlanDropdownOpen = false;
    }
  }

  toggleFileDropdown(event: Event): void {
    event.stopPropagation();
    this.isFileDropdownOpen = !this.isFileDropdownOpen;
  }

  selectUpload(upload: FileUpload): void {
    this.selectedUpload = upload;
    this.planForm.patchValue({ upload_id: upload.id });
    this.isFileDropdownOpen = false;
    
    // Reset image preview state when selecting a new file
    this.showImagePreview = false;
    this.isImageLoading = false;
    this.imageLoadError = false;
  }

  getUploadSecondaryText(upload: FileUpload): string {
    const parts: string[] = [];
    if (upload.mime_type) {
      parts.push(this.getDisplayCategory(upload.mime_type));
    }
    if (upload.created_at) {
      parts.push(this.formatDate(upload.created_at));
    }
    return parts.join(' • ');
  }

  getDisplayCategory(mimeType: string): string {
    if (mimeType?.startsWith('image/')) {
      return 'Bild';
    } else if (mimeType === 'application/pdf') {
      return 'PDF';
    } else if (mimeType?.includes('word') || mimeType?.includes('document')) {
      return 'Dokument';
    } else if (mimeType?.includes('excel') || mimeType?.includes('spreadsheet')) {
      return 'Kalkulation';
    } else {
      return 'Dokument';
    }
  }

  getFormattedIndicatorNumber(indikatorNumber: number | null | undefined): string {
    if (indikatorNumber === null || indikatorNumber === undefined) {
      return '-';
    }
    return indikatorNumber.toString().padStart(3, '0');
  }

  // Image Preview Methods
  isImageFile(upload: FileUpload | null): boolean {
    if (!upload || !upload.mime_type) return false;
    return upload.mime_type.startsWith('image/');
  }

  getFileUrl(upload: FileUpload | null): string {
    if (!upload) return '';
    // Prefer pre-signed URL for images to avoid JWT on <img> requests.
    const id = upload.id as number;
    if (this.isImageFile(upload)) {
      const cache = (this as any)._signedUrlCache || {};
      if (cache[id]) return cache[id];
      this.requestSignedUrl(id, false);
      return '';
    }
    return `${environment.apiUrl}/fileupload/${id}/download`;
  }

  /**
   * Extract base name and extension from filename
   */
  private getFilenameParts(filename: string): { baseName: string, extension: string } | null {
    if (!filename) return null;
    
    const lastDot = filename.lastIndexOf('.');
    if (lastDot === -1) return null;
    
    const baseName = filename.substring(0, lastDot);
    const extension = filename.substring(lastDot + 1);
    
    return { baseName, extension };
  }

  /**
   * Get the interactive plan file (with -iplan suffix) for the current plan
   * Uses cached result to avoid repeated calculations during change detection
   */
  getInteractivePlanFile(): FileUpload | null {
    // Return cached result if available
    if (this.cachedInteractivePlanFile !== undefined) {
      return this.cachedInteractivePlanFile;
    }
    
    const selectedUpload = this.getSelectedUpload();
    if (!selectedUpload || !selectedUpload.original_filename || !this.plan) {
      this.cachedInteractivePlanFile = null;
      return null;
    }
    
    const pathInfo = this.getFilenameParts(selectedUpload.original_filename);
    if (!pathInfo) {
      this.cachedInteractivePlanFile = null;
      return null;
    }
    
    // Construct expected interactive plan filename
    const expectedName = `${pathInfo.baseName}-iplan.${pathInfo.extension}`;
    
    // Search in available uploads for matching file
    // Match by: original_filename matches pattern AND same file_path
    // Filter out files with absolute paths (they're invalid)
    const matchingFiles = this.dropdowns?.uploads?.filter(upload => {
      if (!upload.original_filename || !upload.file_path) return false;
      
      // Skip files with absolute paths (containing /var/www/, /storage/uploads/, or absolute path indicators)
      // Also handle escaped paths (with backslashes)
      const normalizedPath = upload.file_path.replace(/\\/g, '/');
      if (normalizedPath.includes('/var/www/') || 
          normalizedPath.includes('/storage/uploads/') ||
          normalizedPath.includes('/api/src/Action/FileUpload/') ||
          normalizedPath.includes('/api.tigersoft.at/') ||
          normalizedPath.startsWith('/var/') ||
          normalizedPath.startsWith('/usr/') ||
          normalizedPath.startsWith('/home/') ||
          normalizedPath.startsWith('C:/') ||
          normalizedPath.startsWith('D:/')) {
        return false;
      }
      
      // Also skip if file_path doesn't look like a relative path (should start with / and contain date pattern)
      if (!normalizedPath.match(/^\/\d{4}\/\d{2}\/\d{2}\//)) {
        // If it doesn't match the date pattern and is longer than a simple relative path, skip it
        if (normalizedPath.length > 50 || normalizedPath.split('/').length > 10) {
          return false;
        }
      }
      
      // Check if filename matches pattern
      const matchesPattern = upload.original_filename === expectedName;
      
      // Normalize file_path for comparison (extract date portion if needed)
      const normalizedUploadPath = this.normalizeFilePathForComparison(upload.file_path);
      const normalizedSelectedPath = this.normalizeFilePathForComparison(selectedUpload.file_path);
      const matchesPath = normalizedUploadPath === normalizedSelectedPath;
      
      // Optional: Also check table_name and record_id if available
      // For plan files, check if record_id matches (convert both to string for comparison)
      const matchesPlan = !upload.table_name || 
                         (upload.table_name === 'plan' && (
                           !upload.record_id || 
                           !this.plan || 
                           String(upload.record_id) === String(this.plan.id) ||
                           String(upload.record_id) === String(this.plan.bezeichnung)
                         ));
      
      return matchesPattern && matchesPath && matchesPlan;
    }) || [];
    
    // If multiple matches, prefer the newest one with valid file size
    if (matchingFiles.length > 1) {
      matchingFiles.sort((a, b) => {
        // First, prefer files with valid file size (non-zero)
        const aHasSize = a.file_size && a.file_size > 0;
        const bHasSize = b.file_size && b.file_size > 0;
        if (aHasSize !== bHasSize) {
          return bHasSize ? 1 : -1;
        }
        
        // Then by ID (newer files have higher IDs)
        if (a.id !== b.id) {
          return b.id - a.id;
        }
        
        // Finally by uploadDate
        if (a.uploadDate && b.uploadDate) {
          return new Date(b.uploadDate).getTime() - new Date(a.uploadDate).getTime();
        }
        return 0;
      });
    }
    
    // Also filter out files with zero file size if we have alternatives
    const validFiles = matchingFiles.filter(f => f.file_size && f.file_size > 0);
    if (validFiles.length > 0) {
      this.cachedInteractivePlanFile = validFiles[0];
      return this.cachedInteractivePlanFile;
    }
    
    this.cachedInteractivePlanFile = matchingFiles[0] || null;
    return this.cachedInteractivePlanFile;
  }
  
  /**
   * Clear the cached interactive plan file (call when uploads or plan changes)
   */
  private clearInteractivePlanFileCache(): void {
    this.cachedInteractivePlanFile = undefined;
  }

  /**
   * Normalize file_path for comparison by extracting just the date portion
   */
  private normalizeFilePathForComparison(filePath: string): string {
    if (!filePath) return '';
    
    // Extract date pattern (YYYY/MM/DD) from path
    const dateMatch = filePath.match(/(\d{4}\/\d{2}\/\d{2})/);
    if (dateMatch) {
      return '/' + dateMatch[1] + '/';
    }
    
    // If no date pattern, return normalized path (remove leading/trailing slashes, ensure starts with /)
    let normalized = filePath.trim();
    if (normalized && !normalized.startsWith('/')) {
      normalized = '/' + normalized;
    }
    if (normalized && !normalized.endsWith('/')) {
      normalized = normalized + '/';
    }
    return normalized;
  }

  private requestSignedUrl(id: number, download: boolean): void {
    const self: any = this as any;
    if (!self._signedUrlCache) self._signedUrlCache = {};
    
    // Prevent multiple simultaneous requests for the same ID
    if (self._pendingUrlRequests && self._pendingUrlRequests[id]) {
      return; // Request already in progress
    }
    if (!self._pendingUrlRequests) self._pendingUrlRequests = {};
    self._pendingUrlRequests[id] = true;
    
    this.dokumenteService.signDokumentUrl(id, 300, download).subscribe({
      next: (res) => {
        delete self._pendingUrlRequests[id];
        if (res && res.url) {
          // Avoid double /v5 in the final URL when res.url already begins with /v5
          // environment.apiUrl typically ends with /v5
          const base = environment.apiUrl.replace(/\/$/, '').replace(/\/v5$/, '');
          const signedUrl = `${base}${res.url}`;
          self._signedUrlCache[id] = signedUrl;
          
          // Notify the interactive plan component if it exists and this is for the current plan
          // Only update if the URL actually changed to prevent loops
          if (this.interactivePlanComponent && this.getSelectedUpload()?.id === id) {
            const currentUrl = this.interactivePlanComponent.planImageUrl;
            if (currentUrl !== signedUrl) {
              this.interactivePlanComponent.updatePlanImageUrl(signedUrl);
            }
          }
          
          // Trigger change detection to update the template
          this.cdr.detectChanges();
        }
      },
      error: () => {
        delete self._pendingUrlRequests[id];
      }
    });
  }

  toggleImagePreview(): void {
    this.showImagePreview = !this.showImagePreview;
    if (this.showImagePreview) {
      this.isImageLoading = true;
      this.imageLoadError = false;
    }
  }

  onImageLoad(event: Event): void {
    this.isImageLoading = false;
    this.imageLoadError = false;
  }

  onImageError(event: Event): void {
    this.isImageLoading = false;
    this.imageLoadError = true;
    console.error('Error loading image:', event);
  }

  retryImageLoad(): void {
    this.imageLoadError = false;
    this.isImageLoading = true;
    // The image will automatically retry loading when the error state is cleared
  }

  // Plan dropdown methods for interactive preview
  loadAvailablePlans(): void {
    if (!this.currentStandortId) return;
    
    this.planService.getPlansByStandort(this.currentStandortId).subscribe({
      next: (response: any) => {
        // Filter for active plans only
        this.availablePlans = (response.data || response || []).filter((plan: Plan) => plan.active);
        
        // Set current plan as selected if it exists and is active
        if (this.plan && this.plan.active) {
          this.selectedPreviewPlan = this.plan;
        } else if (this.availablePlans.length > 0) {
          this.selectedPreviewPlan = this.availablePlans[0];
        }
      },
      error: (error) => {
        console.error('Error loading available plans:', error);
        this.availablePlans = [];
      }
    });
  }

  togglePlanDropdown(event: Event): void {
    event.stopPropagation();
    this.isPlanDropdownOpen = !this.isPlanDropdownOpen;
  }

  selectPreviewPlan(plan: Plan): void {
    this.selectedPreviewPlan = plan;
    this.isPlanDropdownOpen = false;
    
    // Reset preview image state
    this.isPreviewImageLoading = false;
    this.previewImageLoadError = false;
    
    // Load room indicators for the selected plan's sektion
    if (plan.sektion_id) {
      this.loadRoomIndicatorsForSektion(plan.sektion_id);
    }
  }

  loadRoomIndicatorsForSektion(sektionId: string): void {
    // Load all room indicators for the standort and filter by sektion
    if (!this.currentStandortId) return;
    
    this.objektStrukturService.getRoomIndikatorenByStandort(this.currentStandortId, 200).subscribe({
      next: (response: any) => {
        // Handle both array response and paginated response
        let allIndicators: RoomIndicator[] = [];
        if (Array.isArray(response)) {
          allIndicators = response;
        } else if (response.data && Array.isArray(response.data)) {
          allIndicators = response.data;
        }
        
        // Filter by sektion and only show active indicators
        const filtered = allIndicators.filter(indicator => {
          const statusValue = indicator.status as any; // API can return boolean or number
          const isActive = statusValue === 1 || statusValue === true;
          return indicator.room?.sektion?.id === sektionId && isActive;
        });
        
        // Sort by indikator_number
        this.roomIndicators = this.sortIndicatorsByNumber(filtered);
      },
      error: (error) => {
        console.error('Error loading room indicators for sektion:', error);
        this.roomIndicators = [];
      }
    });
  }

  printRoomIndicator(indicator: RoomIndicator): void {
    this.snackBar.open(`Drucke Raum-Indikator "${indicator.name}"`, 'Schließen', { duration: 3000 });
  }

  /**
   * Check if indicator is effectively active (indicator and all parents are active)
   */
  isIndicatorEffectivelyActive(indicator: RoomIndicator): boolean {
    // Check indicator status
    const statusValue = indicator.status as any;
    if (statusValue !== 1 && statusValue !== true) {
      return false;
    }
    
    // Check room status
    if (!indicator.room || (indicator.room as any).status !== 1 && (indicator.room as any).status !== true) {
      return false;
    }
    
    // Check sektion status
    if (!indicator.room?.sektion || (indicator.room.sektion as any).status !== 1 && (indicator.room.sektion as any).status !== true) {
      return false;
    }
    
    // Check gebaeude status
    if (!indicator.room?.sektion?.gebaeude || (indicator.room.sektion.gebaeude as any).status !== 1 && (indicator.room.sektion.gebaeude as any).status !== true) {
      return false;
    }
    
    return true;
  }

  private sortIndicatorsByNumber(indicators: RoomIndicator[]): RoomIndicator[] {
    return [...indicators].sort((a, b) => {
      const numA = a.indikator_number ?? 999999;
      const numB = b.indikator_number ?? 999999;
      return numA - numB;
    });
  }

  printAllRoomIndicators(): void {
    this.snackBar.open('Drucke alle Raum-Indikatoren', 'Schließen', { duration: 3000 });
  }

  // Preview plan image methods
  getSelectedPreviewPlanUpload(): FileUpload | null {
    if (!this.selectedPreviewPlan?.upload_id || !this.dropdowns?.uploads) return null;
    const uploadIdNum = parseInt(this.selectedPreviewPlan.upload_id.toString(), 10);
    return this.dropdowns.uploads.find(u => u.id === uploadIdNum) || null;
  }

  getPreviewPlanFileUrl(): string {
    const upload = this.getSelectedPreviewPlanUpload();
    if (!upload) return '';
    
    const id = upload.id as number;
    if (this.isImageFile(upload)) {
      const cache = (this as any)._signedUrlCache || {};
      if (cache[id]) return cache[id];
      this.requestSignedUrl(id, false);
      return '';
    }
    return `${environment.apiUrl}/fileupload/${id}/download`;
  }

  onPreviewImageLoad(event: Event): void {
    this.isPreviewImageLoading = false;
    this.previewImageLoadError = false;
  }

  onPreviewImageError(event: Event): void {
    this.isPreviewImageLoading = false;
    this.previewImageLoadError = true;
    console.error('Error loading preview image:', event);
  }

  // Zoom functionality
  zoomIn(): void {
    if (this.zoomLevel < this.maxZoom) {
      this.zoomLevel = Math.min(this.zoomLevel + 25, this.maxZoom);
    }
  }

  zoomOut(): void {
    if (this.zoomLevel > this.minZoom) {
      this.zoomLevel = Math.max(this.zoomLevel - 25, this.minZoom);
    }
  }

  resetZoom(): void {
    this.zoomLevel = 100;
  }

  getZoomPercentage(): string {
    return `${this.zoomLevel}%`;
  }

  getImageStyle(): any {
    return {
      transform: `scale(${this.zoomLevel / 100})`,
      transformOrigin: 'center center',
      transition: 'transform 0.2s ease-in-out'
    };
  }

  getPlacedIndicatorStyle(placedIndicator: PlacedIndicator): any {
    // Calculate position relative to the image container, accounting for zoom
    const zoomFactor = this.zoomLevel / 100;
    const containerPadding = 16; // p-4 = 16px padding
    
    return {
      left: `${placedIndicator.x}%`,
      top: `${placedIndicator.y}%`,
      transform: 'translate(-50%, -50%)',
      transition: placedIndicator.isDragging ? 'none' : 'all 0.2s ease-in-out'
    };
  }

  savePlanPreview(): void {
    if (!this.plan) {
      this.snackBar.open('Kein Plan geladen', 'Schließen', { duration: 3000 });
      return;
    }
    
    this.snackBar.open(`Plan "${this.plan.bezeichnung}" wird gespeichert...`, 'Schließen', { duration: 2000 });
    // Here you could implement actual save functionality
    // For now, just show a success message
    setTimeout(() => {
      this.snackBar.open('Plan erfolgreich gespeichert', 'Schließen', { duration: 3000 });
    }, 2000);
  }

  // Indicator symbol and status methods
  getIndicatorSymbolUrl(indicator: RoomIndicator): string | null {
    if (!indicator.indikator?.symbol_id) return null;
    const key = (environment as any).indikatorSymbolMap?.[indicator.indikator.symbol_id];
    if (!key) return null;
    const base = (environment as any).indikatorSymbolBasePath || '/assets/symbols';
    return `${base}/${key}.svg`;
  }

  getIndicatorSymbolColor(indicator: RoomIndicator): string | null {
    if (!indicator.indikator?.symbol_id) return null;
    const colorMap = (environment as any).indikatorSymbolColorMap || {};
    return colorMap[indicator.indikator.symbol_id] || null;
  }

  getIndicatorStatusTextSimple(status: number | boolean): string {
    const statusValue = status as any; // API can return boolean or number
    return (statusValue === 1 || statusValue === true) ? 'Aktiv' : 'Inaktiv';
  }

  getIndicatorStatusBadgeClasses(status: number | boolean): string {
    const statusValue = status as any; // API can return boolean or number
    if (statusValue === 1 || statusValue === true) {
      return 'bg-green-100 text-green-800';
    }
    return 'bg-red-100 text-red-800';
  }

  // Event handlers for the new Konva-based Interactive Plan Component
  onMarkerPlaced(marker: any): void {
    console.log('Marker placed:', marker);
    // Position is already saved in onMarkerMoved, but we can handle additional logic here if needed
  }
  
  onMarkerMoved(marker: any): void {
    console.log('Marker moved:', marker);
    // Position is now saved directly in the interactive-plan component via objektStrukturService
    // This handler is kept for potential future use or logging
  }

  onMarkerRemoved(markerId: string): void {
    console.log('Marker removed:', markerId);
    // Handle marker removal - could remove from backend here
  }

  onZoomChanged(zoomLevel: number): void {
    console.log('Zoom changed:', zoomLevel);
    this.zoomLevel = zoomLevel;
  }

  onPlanExported(dataUrl: string): void {
    console.log('Plan exported:', dataUrl);
    // Handle plan export - could trigger download here
    const link = document.createElement('a');
    link.download = `plan-${this.plan?.bezeichnung || 'export'}-${new Date().toISOString().split('T')[0]}.png`;
    link.href = dataUrl;
    link.click();
  }

  /**
   * Export plan as PDF
   */
  exportPlanAsPDF(): void {
    if (!this.plan || !this.plan.id) {
      this.snackBar.open('Kein Plan ausgewählt', 'Schließen', { duration: 3000 });
      return;
    }

    this.isExportingPDF = true;

    // Check for saved interactive plan image first
    const savedInteractivePlan = this.getInteractivePlanFile();
    let planImageBase64: string | null = null;
    let useSavedImage = true;

    if (savedInteractivePlan) {
      // Use saved image - API will load it from disk
      planImageBase64 = null;
      useSavedImage = true;
      console.log('Using saved interactive plan image for PDF export');
    } else if (this.interactivePlanComponent) {
      // Fallback: Export from canvas
      try {
        const imageDataUrl = this.interactivePlanComponent.exportAsImage('png', true);
        // Remove data URL prefix to get just base64
        planImageBase64 = imageDataUrl.replace(/^data:image\/\w+;base64,/, '');
        useSavedImage = false;
        console.log('Using canvas export for PDF export');
      } catch (error) {
        console.error('Error exporting from canvas:', error);
        this.snackBar.open('Fehler beim Exportieren des Plans', 'Schließen', { duration: 3000 });
        this.isExportingPDF = false;
        return;
      }
    } else {
      // No interactive plan component available
      this.snackBar.open('Kein interaktiver Plan verfügbar', 'Schließen', { duration: 3000 });
      this.isExportingPDF = false;
      return;
    }

    // Call API endpoint
    this.planService.exportPlanAsPDF(this.plan.id, planImageBase64, useSavedImage).subscribe({
      next: (blob: Blob) => {
        // Download PDF
        this.downloadPDF(blob);
        this.isExportingPDF = false;
        this.snackBar.open('PDF erfolgreich erstellt', 'Schließen', { duration: 3000 });
      },
      error: (error) => {
        console.error('Error exporting plan as PDF:', error);
        this.snackBar.open('Fehler beim Exportieren des Plans als PDF', 'Schließen', { duration: 3000 });
        this.isExportingPDF = false;
      }
    });
  }

  /**
   * Download PDF blob as file
   */
  private downloadPDF(blob: Blob): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `plan-${this.plan?.bezeichnung || this.plan?.id || 'export'}-${new Date().toISOString().split('T')[0]}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  // Method to get the plan image URL for the interactive plan component
  getPlanImageUrl(): string {
    const selectedUpload = this.getSelectedUpload();
    if (!selectedUpload) return '';
    
    // For images, try to get the signed URL from cache first
    if (this.isImageFile(selectedUpload)) {
      const cache = (this as any)._signedUrlCache || {};
      const cachedUrl = cache[selectedUpload.id as number];
      if (cachedUrl) {
        return cachedUrl;
      }
      // If no cached URL, request one
      this.requestSignedUrl(selectedUpload.id as number, false);
      return '';
    }
    
    // For non-images, return the direct download URL
    return `${environment.apiUrl}/fileupload/${selectedUpload.id}/download`;
  }
}
